import React from "react";
import { useParams } from "react-router-dom";
import {
  Box,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import AddClassContentModal from "../../components/AddClassContentModal";
import ExternalLinkRosterModal from "../../components/ExternalLinkRosterModal";
import HowToGuideBox from "../../components/HowToGuideBox";
import {
  fetchSessionContent,
  fetchClassLevelContent,
  addSessionContent,
  addClassLevelContent,
  updateSessionContent,
  deleteSessionContent,
  moveSessionContent,
  downloadContentFile,
  getContentAccessUrl,
  reorderSessionContent,
  reorderClassContent,
  fetchCourseDocumentAssignments,
  removeDocumentAssignment,
  updateDocumentAssignment,
  pushDocumentAssignmentToBank,
} from "../../api/backend";
import AddFromBankModal from "../../components/AddFromBankModal";
import SaveToBankPromptDialog from "../../components/SaveToBankPromptDialog";
import ConfirmDialog from "../../components/ConfirmDialog";
import { moveMenuItems } from "../../components/moveMenu";
import Icon from "./documentsList/Icon";
import DocRow from "./documentsList/DocRow";
import { sessionHeading } from "./sessionsList/sessionsModel";
import {
  buildBankRow,
  buildDocRow,
  countFilters,
  filterBank,
  filterItems,
  getKind,
  isPublished,
} from "./documentsList/documentsModel";
import "./documentsList/documentsList.css";

const isDragged = (dragged, item, bank = false) =>
  Boolean(dragged) && dragged.id === item.id && Boolean(dragged._bank) === bank;

const moveIcon = (kind) => <Icon n={kind === "class" ? "layers" : "cal"} s={15} />;
const CLASS_LABEL = "Class-level documents";

/** Drag-and-drop handlers shared by content and bank rows; `active` is whether this row accepts a drop. */
function rowDragProps({ draggable, onDragStart, onDragEnd, active, onDrop, setDropOver }) {
  return {
    draggable: Boolean(draggable),
    onDragStart,
    onDragEnd,
    onDragOver: (e) => {
      if (!active) return;
      e.preventDefault();
      e.stopPropagation();
      setDropOver(true);
    },
    onDragLeave: () => setDropOver(false),
    onDrop: (e) => {
      if (!active) return;
      e.preventDefault();
      e.stopPropagation();
      setDropOver(false);
      onDrop?.();
    },
  };
}

/** A document stored on the class or a session. */
function DocCard({
  item, sessions, canEdit, publishingId, onTogglePublish, onEdit, onDelete, onManageLinks, onMove, onDownload,
  onDragStart, onDragEnd, dragging, draggedItem, onDropBefore,
  showReorder, onMoveUp, onMoveDown, moveUpDisabled, moveDownDisabled,
}) {
  const [dropOver, setDropOver] = React.useState(false);
  const model = React.useMemo(() => buildDocRow(item), [item]);
  const sessionLocked = item.sessionId
    ? Boolean(sessions?.find((s) => s.id === item.sessionId)?.locked)
    : false;
  const actionsDisabled = !canEdit || sessionLocked;
  const moveLocked = actionsDisabled || Boolean(item.inLearningPlan);

  const menuItems = [
    { icon: <Icon n="eye" s={15} />, label: "Preview", onSelect: () => onDownload(item, true) },
    ...(item.hasFile ? [{ icon: <Icon n="download" s={15} />, label: "Download", onSelect: () => onDownload(item) }] : []),
    ...(!actionsDisabled ? [{ icon: <Icon n="edit" s={15} />, label: "Edit document", onSelect: () => onEdit(item) }] : []),
    ...(!actionsDisabled && model.perStudentLinks
      ? [{ icon: <Icon n="link" s={15} />, label: "Manage student links", onSelect: () => onManageLinks(item) }]
      : []),
    ...moveMenuItems({ item, sessions, onMove, locked: moveLocked, classLabel: CLASS_LABEL, icon: moveIcon }),
    ...(!actionsDisabled
      ? [{ separator: true }, { icon: <Icon n="trash" s={15} />, label: "Delete document", danger: true, onSelect: () => onDelete(item) }]
      : []),
  ];

  return (
    <DocRow
      model={model}
      canEdit={!actionsDisabled}
      onOpen={() => onDownload(item, model.primary === "open")}
      onTogglePublish={() => onTogglePublish(item)}
      publishing={publishingId === item.id}
      menuItems={menuItems}
      dragging={dragging}
      dropOver={dropOver}
      reorder={showReorder ? { upDisabled: moveUpDisabled || moveLocked, downDisabled: moveDownDisabled || moveLocked, onUp: onMoveUp, onDown: onMoveDown } : null}
      dragProps={rowDragProps({
        draggable: !moveLocked,
        onDragStart: () => onDragStart?.(item),
        onDragEnd: () => onDragEnd?.(),
        active: Boolean(onDropBefore) && !moveLocked && draggedItem && !isDragged(draggedItem, item),
        onDrop: onDropBefore,
        setDropOver,
      })}
    />
  );
}

/** A Knowledge Bank document assigned to the class, shown in the class-level list or a session. */
function BankDocCard({
  a, sessions, canEdit, draggable, dragging, onDragStart, onDragEnd, onMove,
  bankEditingId, bankEditTitle, setBankEditTitle, bankEditDescription, setBankEditDescription,
  handleStartBankEdit, handleSaveBankEdit, handleToggleAssignmentFlag, setRemoveDocAssignment,
}) {
  const model = React.useMemo(() => buildBankRow(a), [a]);
  const sessionLocked = a.sessionId ? Boolean(sessions?.find((x) => x.id === a.sessionId)?.locked) : false;
  const moveLocked = !canEdit || sessionLocked || Boolean(a.inLearningPlan);
  const editing = bankEditingId === a.id;

  const menuItems = canEdit
    ? [
        { icon: <Icon n="edit" s={15} />, label: "Edit", onSelect: () => handleStartBankEdit(a) },
        { icon: <Icon n="flag" s={15} />, label: a.required ? "Remove required flag" : "Mark as required", onSelect: () => handleToggleAssignmentFlag(a, "required") },
        { icon: <Icon n="star" s={15} />, label: a.featured ? "Remove featured flag" : "Mark as featured", onSelect: () => handleToggleAssignmentFlag(a, "featured") },
        ...moveMenuItems({ item: a, sessions, onMove, locked: moveLocked, classLabel: CLASS_LABEL, icon: moveIcon }),
        { separator: true },
        { icon: <Icon n="trash" s={15} />, label: "Remove from class", danger: true, onSelect: () => setRemoveDocAssignment(a) },
      ]
    : [];

  const titleSlot = editing ? (
    <div className="edit">
      <input aria-label="Document title" value={bankEditTitle} onChange={(e) => setBankEditTitle(e.target.value)} />
      <input aria-label="Document description" placeholder="Description" value={bankEditDescription} onChange={(e) => setBankEditDescription(e.target.value)} />
    </div>
  ) : null;

  return (
    <DocRow
      model={model}
      canEdit={canEdit}
      onTogglePublish={() => handleToggleAssignmentFlag(a, "enabled")}
      menuItems={menuItems}
      titleSlot={titleSlot}
      extraAction={editing ? <button type="button" className="btn btn-p btn-sm" title="Save" onClick={() => handleSaveBankEdit(a)}>Save</button> : null}
      dragging={dragging}
      dragProps={{
        draggable: Boolean(draggable) && !moveLocked,
        onDragStart: () => onDragStart?.({ ...a, _bank: true }),
        onDragEnd: () => onDragEnd?.(),
      }}
    />
  );
}

// ─── Toolbar ──────────────────────────────────────────────────────────────────
function Toolbar({ query, setQuery, filter, setFilter, counts, canEdit, onFromBank, onUpload }) {
  const chips = canEdit
    ? [{ id: "all", label: "All" }, { id: "published", label: "Published" }, { id: "draft", label: "Drafts" }, { id: "required", label: "Required" }]
    : [{ id: "all", label: "All" }, { id: "required", label: "Required" }];
  return (
    <div className="bar-t">
      <label className="search">
        <Icon n="search" s={15} />
        <input type="search" placeholder="Search documents" aria-label="Search documents" value={query} onChange={(e) => setQuery(e.target.value)} />
      </label>
      <div className="seg" role="group" aria-label="Filter documents">
        {chips.map((c) => (
          <button key={c.id} type="button" className={filter === c.id ? "on" : ""} aria-pressed={filter === c.id}
            disabled={c.id !== "all" && !counts[c.id] && filter !== c.id} onClick={() => setFilter(c.id)}>
            {c.label} <b>{counts[c.id] ?? 0}</b>
          </button>
        ))}
      </div>
      <span className="sp" />
      <HowToGuideBox
        iconOnly
        title="How to Use Course Documents"
        role="Instructors & Course Coordinators"
        steps={[
          {
            number: 1,
            title: "Upload a Document",
            description:
              "Click the upload button to attach a file to this course or session.",
            details: [
              "Supported formats: PDF, Word, Excel, images, and more",
              "Files are stored securely and tied to your course",
            ],
          },
          {
            number: 2,
            title: "Add a Link",
            description:
              "Use 'Add Link' to attach an external URL (e.g., video, website).",
            details:
              "Links appear alongside uploaded files in the document list.",
          },
          {
            number: 3,
            title: "Set Visibility",
            description: "Choose Public or Private for each document.",
            details: [
              "Public: visible to enrolled students",
              "Private: instructors and coordinators only",
            ],
          },
          {
            number: 4,
            title: "Organize by Session",
            description:
              "Assign documents to a specific session or keep them course-level.",
            details:
              "Session-level documents appear only when viewing that session.",
          },
          {
            number: 5,
            title: "Preview or Download",
            description:
              "Use the action menu on any document to preview, download, move, or delete it.",
          },
        ]}
        tips={[
          "Use consistent file naming so students can find materials easily.",
          "Set visibility before sharing — students see public documents immediately.",
          "Course-level documents are visible across all sessions.",
          "Delete outdated documents to keep the list clean.",
        ]}
      />
      {canEdit && onFromBank && (
        <button type="button" className="btn btn-o" onClick={onFromBank}><Icon n="plus" />From Bank</button>
      )}
      {canEdit && (
        <button type="button" className="btn btn-p" onClick={onUpload}><Icon n="upload" />Upload</button>
      )}
    </div>
  );
}

// ─── Session group ────────────────────────────────────────────────────────────
function SessionDocGroup({
  session, items, canEdit, onEdit, onAddDoc, draggedItem, onDragStartItem, onDragEndItem, onDropTarget,
  sessions, onTogglePublish, onDelete, onManageLinks, onMove, onDownload, publishingId, onDropOnItem,
  bankItems, bankRowProps,
}) {
  const [dragOver, setDragOver] = React.useState(false);
  const locked = Boolean(session.locked);
  const editable = canEdit && !locked;
  const canDrop = editable && draggedItem;
  const heading = sessionHeading(session);
  const bank = bankItems || [];

  return (
    <div
      className={`sblk${dragOver ? " over" : ""}`}
      data-testid={`session-docs-${session.id}`}
      onDragOver={(e) => { if (canDrop) { e.preventDefault(); setDragOver(true); } }}
      onDragLeave={(e) => { if (!e.currentTarget.contains(e.relatedTarget)) setDragOver(false); }}
      onDrop={(e) => {
        e.preventDefault();
        setDragOver(false);
        if (canDrop) onDropTarget(session.id);
      }}
    >
      <div className="grp">
        <b>{heading.label}</b>
        {heading.date && <span>{heading.date}</span>}
        {heading.detail && <span>{heading.detail}</span>}
        {locked && <span className="lock"><Icon n="lock" s={12} />Locked</span>}
        {editable && onAddDoc && (
          <button type="button" className="btn btn-o btn-sm" onClick={() => onAddDoc(session)}><Icon n="plus" />Add Document</button>
        )}
      </div>
      {items === undefined ? (
        <div className="none">Loading documents…</div>
      ) : items.length + bank.length === 0 ? (
        <div className="none">No documents attached</div>
      ) : (
        <>
          {items.map((item, idx) => (
            <DocCard
              key={item.id}
              item={item}
              sessions={sessions}
              canEdit={editable}
              publishingId={publishingId}
              onTogglePublish={onTogglePublish}
              onEdit={onEdit}
              onDelete={onDelete}
              onManageLinks={onManageLinks}
              onMove={editable ? onMove : null}
              onDownload={onDownload}
              dragging={isDragged(draggedItem, item)}
              draggedItem={draggedItem}
              onDragStart={() => onDragStartItem(item)}
              onDragEnd={onDragEndItem}
              onDropBefore={() => onDropOnItem(item.id)}
              showReorder={editable && items.length > 1}
              moveUpDisabled={idx === 0}
              moveDownDisabled={idx === items.length - 1}
              onMoveUp={() => onDropOnItem(items[idx - 1]?.id ?? null, item)}
              onMoveDown={() => onDropOnItem(items[idx + 2]?.id ?? null, item)}
            />
          ))}
          {bank.map((a) => (
            <BankDocCard
              key={`bank-${a.id}`}
              a={a}
              {...bankRowProps}
              canEdit={editable}
              draggable={editable}
              dragging={isDragged(draggedItem, a, true)}
            />
          ))}
        </>
      )}
      {dragOver && <div className="drop">Drop here to move this document to {heading.label}</div>}
    </div>
  );
}

export default function DocumentsTab({
  sessions,
  canEdit,
  onDocumentsCountChange,
}) {
  const { id: courseId } = useParams();

  const [classItems, setClassItems] = React.useState([]);
  const [sessionItems, setSessionItems] = React.useState({});
  const [loadingClass, setLoadingClass] = React.useState(true);
  const [, setLoadingSessionId] = React.useState(null);

  const [filter, setFilter] = React.useState("all");
  const [query, setQuery] = React.useState("");
  const [publishingId, setPublishingId] = React.useState(null);

  const [contentModalOpen, setContentModalOpen] = React.useState(false);
  const [contentDialogTarget, setContentDialogTarget] = React.useState("class");
  const [contentEditTarget, setContentEditTarget] = React.useState(null);
  const [saving, setSaving] = React.useState(false);
  const [saveError, setSaveError] = React.useState("");
  const [deleteConfirm, setDeleteConfirm] = React.useState(null);
  const [removeDocAssignment, setRemoveDocAssignment] = React.useState(null);
  const [dragOver, setDragOver] = React.useState(false);
  const [droppedFile, setDroppedFile] = React.useState(null);

  const [bankModalOpen, setBankModalOpen] = React.useState(false);
  const [bankAssignments, setBankAssignments] = React.useState([]);
  const [bankEditingId, setBankEditingId] = React.useState(null);
  const [bankEditTitle, setBankEditTitle] = React.useState("");
  const [bankEditDescription, setBankEditDescription] = React.useState("");
  const [bankPromptAssignmentId, setBankPromptAssignmentId] = React.useState(null);
  const [rosterModalItem, setRosterModalItem] = React.useState(null);
  const [previewDoc, setPreviewDoc] = React.useState(null);

  const [draggedItem, setDraggedItem] = React.useState(null);
  const [classDropOver, setClassDropOver] = React.useState(false);

  React.useEffect(() => {
    fetchCourseDocumentAssignments(courseId)
      .then((d) => setBankAssignments(Array.isArray(d) ? d : []))
      .catch(() => {});
  }, [courseId]);

  React.useEffect(() => {
    setLoadingClass(true);
    fetchClassLevelContent(courseId)
      .then((items) => setClassItems(Array.isArray(items) ? items : []))
      .catch(() => {})
      .finally(() => setLoadingClass(false));
  }, [courseId]);

  const loadSessionContent = async (session) => {
    if (sessionItems[session.id] !== undefined) return;
    setLoadingSessionId(session.id);
    try {
      const items = await fetchSessionContent(session.id);
      setSessionItems((prev) => ({
        ...prev,
        [session.id]: Array.isArray(items) ? items : [],
      }));
    } finally {
      setLoadingSessionId(null);
    }
  };

  React.useEffect(() => {
    (sessions || []).forEach((s) => loadSessionContent(s));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessions]);

  React.useEffect(() => {
    if (typeof onDocumentsCountChange !== "function") return;
    const classCount = Array.isArray(classItems) ? classItems.length : 0;
    const bankCount = Array.isArray(bankAssignments)
      ? bankAssignments.length
      : 0;
    const sessionCountTotal = Object.values(sessionItems).reduce(
      (sum, list) => sum + (Array.isArray(list) ? list.length : 0),
      0,
    );
    onDocumentsCountChange(classCount + bankCount + sessionCountTotal);
  }, [classItems, bankAssignments, sessionItems, onDocumentsCountChange]);

  const openAdd = (target, prefillType) => {
    if (!canEdit) return;
    setContentDialogTarget(target);
    setContentEditTarget(prefillType ? { contentType: prefillType } : null);
    setContentModalOpen(true);
  };

  const openEdit = (item) => {
    if (!canEdit) return;
    setContentDialogTarget(item.classLevel ? "class" : { id: item.sessionId });
    setContentEditTarget(item);
    setContentModalOpen(true);
  };

  const handleContentSave = async (formData) => {
    if (!formData.title?.trim()) return;
    setSaving(true);
    setSaveError("");
    const payload = {
      title: formData.title,
      contentType: formData.contentType,
      description: formData.description,
      url: formData.url || "",
      required: formData.required,
      featured: formData.featured,
      instructorViewOnly: formData.instructorViewOnly,
      tags: JSON.stringify(formData.tags || []),
      attachedSessionIds: JSON.stringify(formData.attachedSessions || []),
      ...(formData.linkScope ? { linkScope: formData.linkScope } : {}),
      ...(formData.embedCode ? { embedCode: formData.embedCode } : {}),
      ...(formData.file ? { file: formData.file } : {}),
    };
    try {
      if (contentEditTarget && contentEditTarget.id) {
        const updated = await updateSessionContent(
          contentEditTarget.id,
          payload,
        );
        if (updated.classLevel) {
          setClassItems((prev) =>
            prev.map((c) => (c.id === updated.id ? updated : c)),
          );
        } else {
          setSessionItems((prev) => ({
            ...prev,
            [updated.sessionId]: (prev[updated.sessionId] || []).map((c) =>
              c.id === updated.id ? updated : c,
            ),
          }));
        }
      } else if (contentDialogTarget === "class") {
        const created = await addClassLevelContent(courseId, payload);
        setClassItems((prev) => [...prev, created]);
      } else {
        const created = await addSessionContent(
          courseId,
          contentDialogTarget.id,
          payload,
        );
        setSessionItems((prev) => ({
          ...prev,
          [contentDialogTarget.id]: [
            ...(prev[contentDialogTarget.id] || []),
            created,
          ],
        }));
      }
      setContentModalOpen(false);
    } catch (e) {
      setSaveError(
        e?.response?.data?.error || "Save failed. Please try again.",
      );
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (item) => {
    if (!canEdit) return;
    try {
      await deleteSessionContent(item.id);
      if (item.classLevel) {
        setClassItems((prev) => prev.filter((c) => c.id !== item.id));
      } else {
        setSessionItems((prev) => ({
          ...prev,
          [item.sessionId]: (prev[item.sessionId] || []).filter(
            (c) => c.id !== item.id,
          ),
        }));
      }
    } finally {
      setDeleteConfirm(null);
    }
  };

  // scopeKey: null = class-level, otherwise a sessionId. Reads/writes the
  // matching bit of state, so class-level and session-level share one code
  // path for move + reorder.
  const getScopeItems = (scopeKey) =>
    scopeKey === null ? classItems : sessionItems[scopeKey] || [];

  const setScopeItems = (scopeKey, updater) => {
    if (scopeKey === null) {
      setClassItems(updater);
    } else {
      setSessionItems((prev) => ({
        ...prev,
        [scopeKey]: updater(prev[scopeKey] || []),
      }));
    }
  };

  /**
   * Moves `item` into `targetScopeKey` (null = class-level, else a sessionId)
   * and places it immediately before `targetItemId` (or at the end if null).
   * Covers plain moves (Move-to menu), drag-and-drop between/within lists,
   * and the up/down reorder arrows — all funnel through here.
   */
  const handleDrop = async (item, targetScopeKey, targetItemId) => {
    if (!canEdit || !item) return;
    if (item._bank) {
      const from = item.sessionId ?? null;
      if (from === targetScopeKey) return;
      const setScope = (scope) =>
        setBankAssignments((prev) =>
          prev.map((a) => (a.id === item.id ? { ...a, sessionId: scope } : a)),
        );
      setScope(targetScopeKey);
      try {
        await updateDocumentAssignment(courseId, item.id, {
          sessionId: targetScopeKey,
        });
      } catch {
        setScope(from);
      }
      return;
    }
    const sourceScopeKey = item.classLevel ? null : item.sessionId;
    const crossScope = sourceScopeKey !== targetScopeKey;

    const prevSource = getScopeItems(sourceScopeKey);
    const prevTarget = crossScope ? getScopeItems(targetScopeKey) : prevSource;

    const targetExisting = prevTarget
      .filter((d) => d.id !== item.id)
      .sort((a, b) => (a.displayOrder ?? 0) - (b.displayOrder ?? 0));
    const insertAt =
      targetItemId == null
        ? targetExisting.length
        : Math.max(
            0,
            targetExisting.findIndex((d) => d.id === targetItemId),
          );

    const movedItem = {
      ...item,
      sessionId: targetScopeKey,
      classLevel: targetScopeKey === null,
    };
    const newOrderIds = [
      ...targetExisting.slice(0, insertAt).map((d) => d.id),
      item.id,
      ...targetExisting.slice(insertAt).map((d) => d.id),
    ];
    const newTargetList = newOrderIds.map((id, i) => {
      const found =
        id === item.id ? movedItem : targetExisting.find((d) => d.id === id);
      return { ...found, displayOrder: i };
    });

    if (crossScope) {
      setScopeItems(sourceScopeKey, (list) =>
        list.filter((d) => d.id !== item.id),
      );
    }
    setScopeItems(targetScopeKey, () => newTargetList);

    try {
      if (crossScope) {
        await moveSessionContent(item.id, targetScopeKey);
      }
      if (targetScopeKey === null) {
        await reorderClassContent(courseId, newOrderIds);
      } else {
        await reorderSessionContent(targetScopeKey, newOrderIds);
      }
    } catch {
      setScopeItems(sourceScopeKey, () => prevSource);
      if (crossScope) setScopeItems(targetScopeKey, () => prevTarget);
    }
  };

  const makeDropHandler = (scopeKey) => (targetItemId, movingItem) =>
    handleDrop(movingItem || draggedItem, scopeKey, targetItemId ?? null);

  const handleDownload = async (item, preview = false) => {
    try {
      const kind = getKind(item);
      if (preview && !item.hasFile && item.url) {
        window.open(item.url, "_blank", "noopener,noreferrer");
        return;
      }
      if (preview && kind === "vid" && item.hasFile) {
        setPreviewDoc({ title: item.title, kind, url: await getContentAccessUrl(item.id) });
        return;
      }
      if (preview && (kind === "img" || kind === "pdf")) {
        setPreviewDoc({ title: item.title, kind, url: await downloadContentFile(item.id) });
        return;
      }
      if (kind === "vid") {
        const url = await getContentAccessUrl(item.id);
        window.open(url, "_blank", "noopener,noreferrer");
        return;
      }
      if (kind === "doc" && /\.pptx?$/i.test(item.fileName || "")) {
        const url = await getContentAccessUrl(item.id);
        window.open(
          `https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(url)}`,
          "_blank",
          "noopener,noreferrer",
        );
        return;
      }
      const url = await downloadContentFile(item.id);
      const a = document.createElement("a");
      a.href = url;
      a.download = item.fileName || item.title;
      a.click();
    } catch {
      // silent
    }
  };

  const handleTogglePublish = async (item) => {
    if (publishingId) return;
    setPublishingId(item.id);
    const wasEnabled = isPublished(item);
    const optimistic = (list) =>
      list.map((c) => (c.id === item.id ? { ...c, enabled: !wasEnabled } : c));
    if (item.classLevel) {
      setClassItems(optimistic);
    } else {
      setSessionItems((prev) => ({
        ...prev,
        [item.sessionId]: optimistic(prev[item.sessionId] || []),
      }));
    }
    try {
      await updateSessionContent(item.id, { enabled: !wasEnabled });
    } catch {
      const revert = (list) =>
        list.map((c) => (c.id === item.id ? { ...c, enabled: wasEnabled } : c));
      if (item.classLevel) {
        setClassItems(revert);
      } else {
        setSessionItems((prev) => ({
          ...prev,
          [item.sessionId]: revert(prev[item.sessionId] || []),
        }));
      }
    } finally {
      setPublishingId(null);
    }
  };

  const handleToggleAssignmentFlag = async (assignment, field) => {
    const prevValue = Boolean(assignment[field]);
    const nextValue = !prevValue;
    setBankAssignments((prev) =>
      prev.map((x) =>
        x.id === assignment.id ? { ...x, [field]: nextValue } : x,
      ),
    );
    try {
      await updateDocumentAssignment(courseId, assignment.id, {
        [field]: nextValue,
      });
    } catch {
      setBankAssignments((prev) =>
        prev.map((x) =>
          x.id === assignment.id ? { ...x, [field]: prevValue } : x,
        ),
      );
    }
  };

  const handleStartBankEdit = (assignment) => {
    const doc = assignment.document || {};
    setBankEditingId(assignment.id);
    setBankEditTitle(assignment.documentTitle || doc.title || assignment.title || "");
    setBankEditDescription(assignment.documentDescription || doc.description || assignment.description || "");
  };

  const handleSaveBankEdit = async (assignment) => {
    const updated = await updateDocumentAssignment(courseId, assignment.id, {
      title: bankEditTitle,
      description: bankEditDescription,
    });
    setBankAssignments((prev) =>
      prev.map((x) => (x.id === updated.id ? updated : x)),
    );
    setBankEditingId(null);
    if (assignment.documentId) setBankPromptAssignmentId(assignment.id);
  };

  const handleBankPromptChoice = async (mode) => {
    const assignmentId = bankPromptAssignmentId;
    setBankPromptAssignmentId(null);
    if (mode === "KEEP_LOCAL" || !assignmentId) return;
    const updated = await pushDocumentAssignmentToBank(courseId, assignmentId, mode);
    setBankAssignments((prev) =>
      prev.map((x) => (x.id === updated.id ? updated : x)),
    );
  };

  // Filtering + sort — shared by the class-level list and every session group.
  const filtered = filterItems(classItems, filter, query);
  const bankForScope = (scopeKey) =>
    filterBank(bankAssignments.filter((a) => (a.sessionId ?? null) === scopeKey), filter, query);
  const classBank = bankForScope(null);
  const classTotal =
    classItems.length + bankAssignments.filter((a) => a.sessionId == null).length;
  const counts = countFilters([classItems, ...Object.values(sessionItems)], bankAssignments);

  const bankRowProps = {
    sessions,
    onMove: (item, targetSessionId) =>
      makeDropHandler(targetSessionId)(null, { ...item, _bank: true }),
    onDragStart: setDraggedItem,
    onDragEnd: () => setDraggedItem(null),
    bankEditingId,
    bankEditTitle,
    setBankEditTitle,
    bankEditDescription,
    setBankEditDescription,
    handleStartBankEdit,
    handleSaveBankEdit,
    handleToggleAssignmentFlag,
    setRemoveDocAssignment,
  };

  const hasFiles = (e) => Array.from(e.dataTransfer?.types || []).includes("Files");
  const listActive = canEdit && (draggedItem || dragOver);

  return (
    <div className="dl-root">
      <Toolbar
        query={query}
        setQuery={setQuery}
        filter={filter}
        setFilter={setFilter}
        counts={counts}
        canEdit={canEdit}
        onFromBank={() => setBankModalOpen(true)}
        onUpload={() => openAdd("class", "DOCUMENT")}
      />

      {/* ── Class-level Documents ── */}
      <section className="sec">
        <div className="sh">
          <div>
            <h2>Class-level Documents</h2>
            <p>
              {filtered.length + classBank.length} of {classTotal} · visible across all sessions
              {canEdit
                ? " · drag to reorder, or onto a session below to move. Drop files here to upload."
                : ". Added by your training center administrator."}
            </p>
          </div>
        </div>

        <div
          className={`list${classDropOver ? " over" : ""}${dragOver ? " files" : ""}`}
          onDragOver={(e) => {
            if (!canEdit) return;
            if (draggedItem) {
              e.preventDefault();
              setClassDropOver(true);
            } else if (hasFiles(e)) {
              e.preventDefault();
              setDragOver(true);
            }
          }}
          onDragLeave={(e) => {
            if (e.currentTarget.contains(e.relatedTarget)) return;
            setClassDropOver(false);
            setDragOver(false);
          }}
          onDrop={(e) => {
            e.preventDefault();
            setClassDropOver(false);
            setDragOver(false);
            if (!canEdit) return;
            if (draggedItem) {
              makeDropHandler(null)(null);
              return;
            }
            const f = e.dataTransfer?.files?.[0];
            if (f) {
              setDroppedFile(f);
              openAdd("class", "DOCUMENT");
            }
          }}
          data-active={listActive ? "true" : undefined}
        >
          {loadingClass ? (
            <div className="empty">Loading documents…</div>
          ) : filtered.length === 0 && classBank.length === 0 ? (
            <div className="empty">
              {query || filter !== "all" ? (
                <>
                  <b>No documents match these filters</b>
                  Try a different status or clear the search.
                </>
              ) : (
                <>
                  <b>No class-level documents yet</b>
                  {canEdit
                    ? "Upload files or add links to make materials available to students."
                    : "Your training center administrator adds documents for this class."}
                </>
              )}
            </div>
          ) : (
            <>
              {filtered.map((item, idx) => (
                <DocCard
                  key={item.id}
                  item={item}
                  sessions={sessions}
                  canEdit={canEdit}
                  publishingId={publishingId}
                  onTogglePublish={handleTogglePublish}
                  onEdit={openEdit}
                  onDelete={setDeleteConfirm}
                  onManageLinks={setRosterModalItem}
                  onMove={canEdit ? (it, targetSessionId) => makeDropHandler(targetSessionId)(null, it) : null}
                  onDownload={handleDownload}
                  dragging={isDragged(draggedItem, item)}
                  draggedItem={draggedItem}
                  onDragStart={() => setDraggedItem(item)}
                  onDragEnd={() => setDraggedItem(null)}
                  onDropBefore={() => makeDropHandler(null)(item.id)}
                  showReorder={canEdit && filtered.length > 1}
                  moveUpDisabled={idx === 0}
                  moveDownDisabled={idx === filtered.length - 1}
                  onMoveUp={() => makeDropHandler(null)(filtered[idx - 1]?.id ?? null, item)}
                  onMoveDown={() => makeDropHandler(null)(filtered[idx + 2]?.id ?? null, item)}
                />
              ))}
              {classBank.map((a) => (
                <BankDocCard
                  key={`bank-${a.id}`}
                  a={a}
                  {...bankRowProps}
                  canEdit={canEdit}
                  draggable={canEdit}
                  dragging={isDragged(draggedItem, a, true)}
                />
              ))}
            </>
          )}
        </div>
      </section>

      {/* ── Session Documents ── */}
      <section className="sec">
        <div className="sh">
          <div>
            <h2>Session Documents</h2>
            <p>Handouts and references for a specific session.</p>
          </div>
        </div>

        {(sessions || []).length === 0 ? (
          <div className="list">
            <div className="empty">No sessions found for this course.</div>
          </div>
        ) : (
          <div className="list">
            {(sessions || []).map((s) => (
              <SessionDocGroup
                key={s.id}
                session={s}
                items={sessionItems[s.id] === undefined ? undefined : filterItems(sessionItems[s.id], filter, query)}
                canEdit={canEdit}
                onEdit={openEdit}
                onAddDoc={(session) => openAdd({ id: session.id })}
                draggedItem={draggedItem}
                onDragStartItem={setDraggedItem}
                onDragEndItem={() => setDraggedItem(null)}
                onDropTarget={() => makeDropHandler(s.id)(null)}
                sessions={sessions}
                onTogglePublish={handleTogglePublish}
                onDelete={setDeleteConfirm}
                onManageLinks={setRosterModalItem}
                onMove={(item, targetSessionId) => makeDropHandler(targetSessionId)(null, item)}
                onDownload={handleDownload}
                publishingId={publishingId}
                onDropOnItem={makeDropHandler(s.id)}
                bankItems={bankForScope(s.id)}
                bankRowProps={bankRowProps}
              />
            ))}
          </div>
        )}
      </section>

      <ExternalLinkRosterModal
        open={!!rosterModalItem}
        item={rosterModalItem}
        onClose={() => setRosterModalItem(null)}
      />

      {/* ── Add/Edit Modal ── */}
      <AddClassContentModal
        open={contentModalOpen}
        onClose={() => {
          setContentModalOpen(false);
          setSaveError("");
          setDroppedFile(null);
        }}
        onSave={handleContentSave}
        sessions={sessions || []}
        editTarget={contentEditTarget?.id ? contentEditTarget : null}
        dialogTarget={contentDialogTarget}
        saving={saving}
        saveError={saveError}
        prefillFile={droppedFile}
      />

      <Dialog
        open={!!previewDoc}
        onClose={() => setPreviewDoc(null)}
        maxWidth="lg"
        fullWidth
        PaperProps={{ sx: { height: "85vh" } }}
      >
        <DialogTitle sx={{ fontWeight: 700, fontFamily: "var(--font-display)", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          {previewDoc?.title}
          <Button onClick={() => setPreviewDoc(null)} sx={{ textTransform: "none" }}>
            Close
          </Button>
        </DialogTitle>
        <Box sx={{ flex: 1, minHeight: 0, display: "flex", justifyContent: "center", bgcolor: "#000" }}>
          {previewDoc?.kind === "img" ? (
            <img src={previewDoc.url} alt={previewDoc.title} style={{ maxWidth: "100%", maxHeight: "100%", objectFit: "contain" }} />
          ) : previewDoc?.kind === "vid" ? (
            <video src={previewDoc.url} controls autoPlay style={{ width: "100%", height: "100%" }} />
          ) : previewDoc ? (
            <iframe title={previewDoc.title} src={previewDoc.url} style={{ width: "100%", height: "100%", border: "none" }} />
          ) : null}
        </Box>
      </Dialog>

      {/* ── Delete Confirm ── */}
      <Dialog
        open={!!deleteConfirm}
        onClose={() => setDeleteConfirm(null)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle
          sx={{ fontWeight: 700, fontFamily: "var(--font-display)" }}
        >
          Delete Document?
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            Remove <strong>{deleteConfirm?.title}</strong>? This cannot be
            undone.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 2, pb: 2 }}>
          <Button
            onClick={() => setDeleteConfirm(null)}
            sx={{ textTransform: "none" }}
          >
            Cancel
          </Button>
          <Button
            onClick={() => handleDelete(deleteConfirm)}
            variant="contained"
            color="error"
            sx={{ textTransform: "none", fontWeight: 700, borderRadius: "8px" }}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      <ConfirmDialog
        open={!!removeDocAssignment}
        title="Remove document from this class?"
        message={<>
          <b>“{removeDocAssignment?.documentTitle || removeDocAssignment?.document?.title || removeDocAssignment?.title}”</b> will
          be taken off this class, along with any changes made to it here. It stays in your Knowledge Bank.
        </>}
        confirmLabel="Remove"
        busyLabel="Removing…"
        onConfirm={async () => {
          await removeDocumentAssignment(courseId, removeDocAssignment.id);
          setBankAssignments((p) => p.filter((x) => x.id !== removeDocAssignment.id));
        }}
        onClose={() => setRemoveDocAssignment(null)}
      />

      <AddFromBankModal
        open={bankModalOpen}
        onClose={() => setBankModalOpen(false)}
        courseId={courseId}
        type="documents"
        onAdded={() => {
          fetchClassLevelContent(courseId).then((items) =>
            setClassItems(Array.isArray(items) ? items : []),
          );
          fetchCourseDocumentAssignments(courseId).then((d) =>
            setBankAssignments(Array.isArray(d) ? d : []),
          );
        }}
      />

      <SaveToBankPromptDialog
        open={!!bankPromptAssignmentId}
        sourceLabel="document"
        onClose={() => setBankPromptAssignmentId(null)}
        onChoose={handleBankPromptChoice}
      />
    </div>
  );
}
